/**
 * CDC CVX (Vaccines Administered) Code Mappings
 *
 * CVX codes are vaccine product codes maintained by the CDC for use in
 * immunization information systems and HL7 data transmission.
 *
 * @see https://www2a.cdc.gov/vaccines/iis/iisstandards/vaccines.asp?rpt=cvx
 * @see https://www.cdc.gov/vaccines/hcp/imz-schedules/adult-age.html
 * @see https://www.cdc.gov/iis/code-sets/index.html
 *
 * Last updated: 2025-01 (based on 2025 ACIP recommendations and CDC IIS standards)
 *
 * Note: This includes both active and inactive CVX codes to support historical
 * immunization records. Inactive codes may represent discontinued products.
 */

export const CVX_SYSTEM = 'http://hl7.org/fhir/sid/cvx'

/**
 * Vaccine family groupings based on CDC vaccine groups
 * Per 2025 ACIP guidance, Pneumococcal vaccines (PCV and PPSV) are combined
 */
export type VaccineFamily =
  | 'COVID-19'
  | 'Influenza'
  | 'HPV'
  | 'Hepatitis A'
  | 'Hepatitis B'
  | 'Hib'
  | 'MMR'
  | 'Meningococcal (ACWY)'
  | 'Meningococcal (B)'
  | 'Mpox'
  | 'Pneumococcal'
  | 'RSV'
  | 'Tetanus/Diphtheria (Td)'
  | 'Tdap'
  | 'Varicella'
  | 'Zoster (Shingles)'

export interface CVXCodeInfo {
  code: string
  family: VaccineFamily
  display: string
  active: boolean
  notes?: string
}

export interface CvxVaccineGroups {
  [cvxCode: string]: CVXCodeInfo
}

/**
 * Comprehensive CVX code to vaccine family mapping
 * Includes both active and inactive codes for historical record support
 */
export const CVX_TO_VACCINE_GROUP: CvxVaccineGroups = {
  // COVID-19 vaccines (active product codes - not group code 213)
  '300': {
    code: '300',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, PF, 30 mcg/0.3 mL dose',
    active: false,
    notes: 'Pfizer-BioNTech, 12+ years, original formulation',
  },
  '301': {
    code: '301',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, PF, 10 mcg/0.2 mL dose',
    active: false,
    notes: 'Pfizer-BioNTech, pediatric',
  },
  '302': {
    code: '302',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, PF, 3 mcg/0.2 mL dose',
    active: false,
    notes: 'Pfizer-BioNTech, pediatric',
  },
  '306': {
    code: '306',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, PF, 0.1 mg/mL dose',
    active: false,
    notes: 'Moderna, original formulation',
  },
  '309': {
    code: '309',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, bivalent, PF, 30 mcg/0.3 mL',
    active: false,
    notes: 'Pfizer-BioNTech, bivalent',
  },
  '310': {
    code: '310',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, bivalent, PF, 10 mcg/0.2 mL',
    active: false,
    notes: 'Pfizer-BioNTech, pediatric bivalent',
  },
  '311': {
    code: '311',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, bivalent, PF, 3 mcg/0.2 mL',
    active: false,
    notes: 'Pfizer-BioNTech, pediatric bivalent',
  },
  '312': {
    code: '312',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, bivalent, PF, 50 mcg/0.5 mL',
    active: false,
    notes: 'Moderna, bivalent',
  },
  '313': {
    code: '313',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, bivalent, PF, 25 mcg/0.25 mL',
    active: false,
    notes: 'Moderna, pediatric bivalent',
  },
  '314': {
    code: '314',
    family: 'COVID-19',
    display: 'COVID-19, protein subunit, AS03, PF',
    active: false,
    notes: 'Novavax',
  },
  '315': {
    code: '315',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, PF, 30 mcg/0.3 mL, preservative-free',
    active: false,
    notes: 'Pfizer-BioNTech',
  },
  '317': {
    code: '317',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, PF, 10 mcg/0.2 mL, tris-sucrose',
    active: false,
    notes: 'Pfizer-BioNTech, pediatric',
  },
  '318': {
    code: '318',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, PF, 3 mcg/0.2 mL, tris-sucrose',
    active: false,
    notes: 'Pfizer-BioNTech, pediatric',
  },
  '319': {
    code: '319',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, PF, 50 mcg/0.5 mL',
    active: false,
    notes: 'Moderna',
  },
  '323': {
    code: '323',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, monovalent 2024-2025, PF, 30 mcg/0.3 mL',
    active: true,
    notes: 'Pfizer-BioNTech, 2024-2025 formulation',
  },
  '324': {
    code: '324',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, monovalent 2024-2025, PF, 10 mcg/0.2 mL',
    active: true,
    notes: 'Pfizer-BioNTech, pediatric 2024-2025',
  },
  '325': {
    code: '325',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, monovalent 2024-2025, PF, 3 mcg/0.2 mL',
    active: true,
    notes: 'Pfizer-BioNTech, pediatric 2024-2025',
  },
  '326': {
    code: '326',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, monovalent 2024-2025, PF, 50 mcg/0.5 mL',
    active: true,
    notes: 'Moderna, 2024-2025 formulation',
  },
  '328': {
    code: '328',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, monovalent 2024-2025, PF, 25 mcg/0.25 mL',
    active: true,
    notes: 'Moderna, pediatric 2024-2025',
  },
  '329': {
    code: '329',
    family: 'COVID-19',
    display: 'COVID-19, protein subunit, AS03, monovalent 2024-2025, PF',
    active: true,
    notes: 'Novavax, 2024-2025',
  },
  '330': {
    code: '330',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, bivalent, PF, 30 mcg/0.3 mL',
    active: false,
    notes: 'Pfizer-BioNTech, bivalent',
  },
  '338': {
    code: '338',
    family: 'COVID-19',
    display: 'COVID-19, mRNA, LNP-S, monovalent 2025-2026, PF',
    active: true,
    notes: '2025-2026 formulation',
  },

  // Influenza vaccines (trivalent formulations for 2024-25 season)
  '135': {
    code: '135',
    family: 'Influenza',
    display: 'influenza, high dose seasonal, preservative-free',
    active: true,
  },
  '140': {
    code: '140',
    family: 'Influenza',
    display: 'Influenza, seasonal, injectable, preservative free',
    active: true,
  },
  '141': {
    code: '141',
    family: 'Influenza',
    display: 'Influenza, seasonal, injectable',
    active: true,
  },
  '144': {
    code: '144',
    family: 'Influenza',
    display: 'seasonal influenza, intradermal, preservative free',
    active: true,
  },
  '149': {
    code: '149',
    family: 'Influenza',
    display: 'influenza, live, intranasal, quadrivalent',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '150': {
    code: '150',
    family: 'Influenza',
    display: 'Influenza, injectable, quadrivalent, preservative free',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '153': {
    code: '153',
    family: 'Influenza',
    display: 'Influenza, injectable, MDCK, preservative free',
    active: true,
  },
  '155': {
    code: '155',
    family: 'Influenza',
    display: 'Seasonal, trivalent, recombinant, injectable influenza vaccine',
    active: true,
  },
  '158': {
    code: '158',
    family: 'Influenza',
    display: 'influenza, injectable, quadrivalent',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '161': {
    code: '161',
    family: 'Influenza',
    display:
      'Influenza, injectable, quadrivalent, preservative free, pediatric',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '166': {
    code: '166',
    family: 'Influenza',
    display: 'influenza, intradermal, quadrivalent, preservative free',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '168': {
    code: '168',
    family: 'Influenza',
    display: 'Seasonal trivalent influenza vaccine, adjuvanted',
    active: true,
  },
  '171': {
    code: '171',
    family: 'Influenza',
    display: 'Influenza, injectable, MDCK, preservative free, quadrivalent',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '185': {
    code: '185',
    family: 'Influenza',
    display:
      'Seasonal, quadrivalent, recombinant, injectable influenza vaccine',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '186': {
    code: '186',
    family: 'Influenza',
    display: 'Influenza, injectable, MDCK, quadrivalent, preservative',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '197': {
    code: '197',
    family: 'Influenza',
    display: 'influenza, high-dose, quadrivalent',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '205': {
    code: '205',
    family: 'Influenza',
    display: 'influenza, seasonal vaccine, quadrivalent, adjuvanted',
    active: false,
    notes: 'Quadrivalent formulation discontinued',
  },
  '331': {
    code: '331',
    family: 'Influenza',
    display: 'Influenza, Southern Hemisphere, trivalent',
    active: true,
    notes: 'Southern Hemisphere formulation',
  },
  '333': {
    code: '333',
    family: 'Influenza',
    display: 'influenza, live, intranasal, trivalent, self/caregiver admin',
    active: true,
    notes: 'New for self-administration',
  },

  // HPV vaccines
  '62': {
    code: '62',
    family: 'HPV',
    display: 'HPV, quadrivalent',
    active: false,
    notes: 'Gardasil, discontinued',
  },
  '118': {
    code: '118',
    family: 'HPV',
    display: 'HPV, bivalent',
    active: false,
    notes: 'Cervarix, discontinued in US',
  },
  '165': {
    code: '165',
    family: 'HPV',
    display: 'HPV9',
    active: true,
    notes: 'Gardasil 9, current standard',
  },

  // Hepatitis A
  '31': {
    code: '31',
    family: 'Hepatitis A',
    display: 'hepatitis A, pediatric/adolescent dosage',
    active: true,
  },
  '83': {
    code: '83',
    family: 'Hepatitis A',
    display: 'hepatitis A, adult dosage',
    active: true,
  },
  '85': {
    code: '85',
    family: 'Hepatitis A',
    display: 'hepatitis A, unspecified formulation',
    active: true,
  },

  // Hepatitis B
  '08': {
    code: '08',
    family: 'Hepatitis B',
    display: 'hepatitis B, pediatric or pediatric/adolescent dosage',
    active: true,
  },
  '43': {
    code: '43',
    family: 'Hepatitis B',
    display: 'hepatitis B, adult dosage',
    active: true,
  },
  '44': {
    code: '44',
    family: 'Hepatitis B',
    display: 'hepatitis B, dialysis dosage',
    active: true,
  },
  '45': {
    code: '45',
    family: 'Hepatitis B',
    display: 'hepatitis B, unspecified formulation',
    active: true,
  },
  '189': {
    code: '189',
    family: 'Hepatitis B',
    display: 'Hepatitis B, adjuvanted',
    active: true,
    notes: 'Heplisav-B',
  },

  // Haemophilus influenzae type b (Hib)
  '46': {
    code: '46',
    family: 'Hib',
    display: 'Hib (PRP-D)',
    active: false,
  },
  '47': {
    code: '47',
    family: 'Hib',
    display: 'Hib (HbOC)',
    active: false,
  },
  '48': {
    code: '48',
    family: 'Hib',
    display: 'Hib (PRP-T)',
    active: true,
  },
  '49': {
    code: '49',
    family: 'Hib',
    display: 'Hib (PRP-OMP)',
    active: true,
  },
  '50': {
    code: '50',
    family: 'Hib',
    display: 'Hib conjugate, unspecified formulation',
    active: true,
  },
  '51': {
    code: '51',
    family: 'Hib',
    display: 'Hib-Hepatitis B',
    active: true,
  },
  '120': {
    code: '120',
    family: 'Hib',
    display: 'Hib (PRP-T) combination',
    active: true,
  },
  '148': {
    code: '148',
    family: 'Hib',
    display: 'Hib (PRP-OMP) combination',
    active: true,
  },

  // MMR (Measles, Mumps, Rubella)
  '03': {
    code: '03',
    family: 'MMR',
    display: 'MMR',
    active: true,
  },
  '04': {
    code: '04',
    family: 'MMR',
    display: 'M/R',
    active: true,
    notes: 'Measles and rubella only',
  },
  '94': {
    code: '94',
    family: 'MMR',
    display: 'MMRV',
    active: true,
    notes: 'MMR + Varicella',
  },

  // Meningococcal ACWY
  '103': {
    code: '103',
    family: 'Meningococcal (ACWY)',
    display: 'meningococcal polysaccharide (MPSV4)',
    active: false,
  },
  '108': {
    code: '108',
    family: 'Meningococcal (ACWY)',
    display: 'meningococcal ACWY, unspecified formulation',
    active: true,
  },
  '114': {
    code: '114',
    family: 'Meningococcal (ACWY)',
    display: 'meningococcal MCV4P (Menactra)',
    active: true,
  },
  '136': {
    code: '136',
    family: 'Meningococcal (ACWY)',
    display: 'meningococcal MCV4O (Menveo)',
    active: true,
  },
  '167': {
    code: '167',
    family: 'Meningococcal (ACWY)',
    display:
      'meningococcal vaccine of unknown formulation and unknown serogroups',
    active: true,
  },
  '203': {
    code: '203',
    family: 'Meningococcal (ACWY)',
    display: 'meningococcal conjugate (MenQuadfi)',
    active: true,
  },

  // Meningococcal B
  '162': {
    code: '162',
    family: 'Meningococcal (B)',
    display: 'meningococcal B, recombinant (Trumenba)',
    active: true,
  },
  '163': {
    code: '163',
    family: 'Meningococcal (B)',
    display: 'meningococcal B, OMV (Bexsero)',
    active: true,
  },
  '164': {
    code: '164',
    family: 'Meningococcal (B)',
    display: 'meningococcal B, unspecified formulation',
    active: true,
  },

  // Mpox (Monkeypox)
  '206': {
    code: '206',
    family: 'Mpox',
    display: 'Smallpox monkeypox vaccine (Jynneos)',
    active: true,
  },
  '227': {
    code: '227',
    family: 'Mpox',
    display: 'Smallpox and monkeypox vaccine, attenuated vaccinia virus',
    active: true,
  },
  '228': {
    code: '228',
    family: 'Mpox',
    display: 'Mpox (monkeypox) vaccine',
    active: true,
  },

  // Pneumococcal (combined PCV and PPSV per 2025 CDC guidance)
  '33': {
    code: '33',
    family: 'Pneumococcal',
    display: 'pneumococcal polysaccharide PPV23',
    active: true,
    notes: 'PPSV23, Pneumovax 23',
  },
  '100': {
    code: '100',
    family: 'Pneumococcal',
    display: 'pneumococcal conjugate PCV 7',
    active: false,
    notes: 'Prevnar 7, discontinued',
  },
  '109': {
    code: '109',
    family: 'Pneumococcal',
    display: 'pneumococcal, unspecified formulation',
    active: true,
  },
  '133': {
    code: '133',
    family: 'Pneumococcal',
    display: 'pneumococcal conjugate PCV 13',
    active: true,
    notes: 'Prevnar 13',
  },
  '152': {
    code: '152',
    family: 'Pneumococcal',
    display: 'Pneumococcal Conjugate, unspecified formulation',
    active: true,
  },
  '215': {
    code: '215',
    family: 'Pneumococcal',
    display: 'pneumococcal conjugate PCV 15',
    active: true,
    notes: 'Vaxneuvance',
  },
  '216': {
    code: '216',
    family: 'Pneumococcal',
    display: 'pneumococcal conjugate PCV 20',
    active: true,
    notes: 'Prevnar 20',
  },
  '327': {
    code: '327',
    family: 'Pneumococcal',
    display: 'pneumococcal conjugate PCV 21',
    active: true,
    notes: 'Capvaxive',
  },

  // RSV (Respiratory Syncytial Virus)
  '307': {
    code: '307',
    family: 'RSV',
    display: 'RSV, mAb, nirsevimab-alip',
    active: true,
    notes: 'Monoclonal antibody for infants (Beyfortus)',
  },
  '334': {
    code: '334',
    family: 'RSV',
    display: 'RSV, recombinant, protein subunit vaccine',
    active: true,
    notes: 'RSVpreF (Abrysvo)',
  },
  '335': {
    code: '335',
    family: 'RSV',
    display: 'RSV, recombinant, adjuvanted vaccine',
    active: true,
    notes: 'RSVPreF3 (Arexvy)',
  },

  // Tetanus/Diphtheria (Td)
  '09': {
    code: '09',
    family: 'Tetanus/Diphtheria (Td)',
    display: 'Td (adult), preservative free',
    active: true,
  },
  '113': {
    code: '113',
    family: 'Tetanus/Diphtheria (Td)',
    display: 'Td (adult)',
    active: true,
  },
  '139': {
    code: '139',
    family: 'Tetanus/Diphtheria (Td)',
    display: 'Td (adult) unspecified formulation',
    active: true,
  },

  // Tdap (Tetanus, Diphtheria, acellular Pertussis)
  '115': {
    code: '115',
    family: 'Tdap',
    display: 'Tdap',
    active: true,
    notes: 'Boostrix or Adacel',
  },
  '223': {
    code: '223',
    family: 'Tdap',
    display: 'Tdap, preservative free',
    active: true,
  },

  // Varicella (Chickenpox)
  '21': {
    code: '21',
    family: 'Varicella',
    display: 'varicella',
    active: true,
  },
  // Note: '94' (MMRV) is listed under MMR as it's a combination vaccine

  // Zoster (Shingles)
  '121': {
    code: '121',
    family: 'Zoster (Shingles)',
    display: 'zoster vaccine, live',
    active: false,
    notes: 'Zostavax, discontinued 2020',
  },
  '187': {
    code: '187',
    family: 'Zoster (Shingles)',
    display: 'zoster vaccine recombinant',
    active: true,
    notes: 'Shingrix, current standard',
  },
  '188': {
    code: '188',
    family: 'Zoster (Shingles)',
    display: 'zoster, recombinant, adjuvanted',
    active: true,
  },
}

/**
 * Get vaccine family from CVX code
 */
export function getVaccineFamilyFromCVX(cvxCode: string): VaccineFamily | null {
  return CVX_TO_VACCINE_GROUP[cvxCode]?.family ?? null
}

/**
 * Get all CVX codes for a vaccine family
 */
export function getCVXCodesForFamily(family: VaccineFamily): string[] {
  return Object.entries(CVX_TO_VACCINE_GROUP)
    .filter(([_, info]) => info.family === family)
    .map(([code]) => code)
}

/**
 * Get active CVX codes for a vaccine family
 */
export function getActiveCVXCodesForFamily(family: VaccineFamily): string[] {
  return Object.entries(CVX_TO_VACCINE_GROUP)
    .filter(([_, info]) => info.family === family && info.active)
    .map(([code]) => code)
}

/**
 * Check if a CVX code is active
 */
export function isCVXCodeActive(cvxCode: string): boolean {
  return CVX_TO_VACCINE_GROUP[cvxCode]?.active ?? false
}

/**
 * Extract vaccine family from display name using fuzzy string matching
 *
 * This is a fallback method for when CVX code lookup fails.
 * Less reliable than CVX code lookup due to variations in naming.
 *
 * @param display - The vaccine display name/description
 * @returns The matched vaccine family or null if no match found
 */
export function getVaccineFamilyFromDisplay(
  display: string | undefined
): VaccineFamily | null {
  if (!display) {
    return null
  }
  const lower = display.toLowerCase()

  // CDC Adult vaccine families
  if (lower.includes('covid') || lower.includes('sars-cov-2')) {
    return 'COVID-19'
  }
  if (lower.includes('influenza') || lower.includes('flu')) {
    return 'Influenza'
  }
  if (lower.includes('hepatitis a') || lower.includes('hep a')) {
    return 'Hepatitis A'
  }
  if (lower.includes('hepatitis b') || lower.includes('hep b')) {
    return 'Hepatitis B'
  }
  if (lower.includes('hpv') || lower.includes('papillomavirus')) {
    return 'HPV'
  }
  if (
    lower.includes('mmr') ||
    (lower.includes('measles') && lower.includes('mumps'))
  ) {
    return 'MMR'
  }
  if (lower.includes('tdap') && !lower.includes('dtap')) {
    return 'Tdap'
  }
  if (
    lower.includes('tetanus') &&
    lower.includes('diphtheria') &&
    !lower.includes('pertussis') &&
    !lower.includes('tdap')
  ) {
    return 'Tetanus/Diphtheria (Td)'
  }
  // Note: Per 2025 CDC guidance, PCV and PPSV are now combined into 'Pneumococcal'
  if (
    lower.includes('pneumococcal') ||
    lower.includes('pcv') ||
    lower.includes('ppsv')
  ) {
    return 'Pneumococcal'
  }
  if (lower.includes('varicella') || lower.includes('chickenpox')) {
    return 'Varicella'
  }
  if (
    lower.includes('meningococcal') &&
    (lower.includes('acwy') || lower.includes('menacwy'))
  ) {
    return 'Meningococcal (ACWY)'
  }
  if (
    lower.includes('meningococcal') &&
    (lower.includes('b') || lower.includes('menb')) &&
    !lower.includes('acwy')
  ) {
    return 'Meningococcal (B)'
  }
  if (
    lower.includes('meningococcal') &&
    !lower.includes('acwy') &&
    !lower.includes('b')
  ) {
    // Generic meningococcal, default to ACWY
    return 'Meningococcal (ACWY)'
  }
  if (lower.includes('hib') || lower.includes('haemophilus')) {
    return 'Hib'
  }
  if (lower.includes('mpox') || lower.includes('monkeypox')) {
    return 'Mpox'
  }
  if (lower.includes('rsv') || lower.includes('respiratory syncytial')) {
    return 'RSV'
  }
  if (
    lower.includes('zoster') ||
    lower.includes('shingles') ||
    lower.includes('zos')
  ) {
    return 'Zoster (Shingles)'
  }

  return null
}
